// src/layouts/sidebar.tsx

import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../security/AuthLogin";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faLandmark,
  faUsers,
  faTrophy,
  faTable,
  faChartBar,
  faCogs,
  faEye,
  faHeart,
  faChartLine,
  faTools,
  faGavel,
  faPowerOff,
  faFolder,
  faGear,
  faPaste,
  faBook,
} from "@fortawesome/free-solid-svg-icons";
import { io, Socket } from "socket.io-client"; // Asegúrate de tener instalado socket.io-client

/**
 * Estructura que devuelve el endpoint /api/soportes/contadores-rapidos
 */
interface SoportesContadores {
  countFavoritos: number;
  countBajasEstadisticas: number;
  countSoportesTecnicos: number;
  countSoportesAdministrativos: number;
  countSoportesJuridicos: number;
}

/**
 * Llamada a /api/soportes/contadores-rapidos para obtener los contadores
 */
async function fetchSoportesContadoresRapidos(): Promise<SoportesContadores> {
  const token = localStorage.getItem("authToken");
  if (!token) throw new Error("No token");

  const resp = await fetch(
    `${`${window.location.protocol}//${window.location.hostname}`}/api/soportes/contadores-rapidos`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!resp.ok) {
    throw new Error(`Error al obtener contadores rapidos: ${resp.statusText}`);
  }
  const json = await resp.json();
  return json.data;
}

function LayoutWithSidebar() {
  const { userRole, logout } = useAuth();
  const navigate = useNavigate();

  // Estado local con los contadores
  const [counts, setCounts] = useState<SoportesContadores>({
    countFavoritos: 0,
    countBajasEstadisticas: 0,
    countSoportesTecnicos: 0,
    countSoportesAdministrativos: 0,
    countSoportesJuridicos: 0,
  });

  // Referencia para la instancia del socket
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // 1) Conectar con el servidor Socket.io
    // RECOMENDADO (explícito con origin):
    const socket = io(window.location.origin, {
      path: "/socket.io",
      transports: ["websocket"],
      withCredentials: true,
      auth: (cb) => cb({ token: localStorage.getItem("authToken") }),
    });

    socketRef.current = socket;

    // 2) Cargar contadores la primera vez
    fetchSoportesContadoresRapidos()
      .then((data) => {
        setCounts(data);
      })
      .catch((err) =>
        console.error("Error al obtener contadores iniciales:", err)
      );

    // 3) Escuchar el evento "actualizarContadores"
    socket.on("actualizarContadores", async () => {
      try {
        const data = await fetchSoportesContadoresRapidos();
        setCounts(data);
      } catch (err) {
        console.error("Error actualizando contadores tras evento socket:", err);
      }
    });

    // 4) Limpiar (desconectar) al desmontar
    return () => {
      socket.off("actualizarContadores");
      socket.disconnect();
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  /**
   * Componente para mostrar un "badge" rojo si number > 0
   */
  function Badge({ number }: { number: number }) {
    if (number <= 0) return null;
    return (
      <span
        className="oclem-header__badge"
      >
        {number}
      </span>
    );
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        <ul>
          {/* Menú extra si admin */}
          {userRole === "admin" && (
            <>
              <NavLink to="/" end>
                <li>
                  <FontAwesomeIcon icon={faHome} /> Inicio
                </li>
              </NavLink>
              <NavLink to="/concursos">
                <li>
                  <FontAwesomeIcon icon={faLandmark} /> Concursos
                </li>
              </NavLink>
              <NavLink to="/adjudicaciones">
                <li>
                  <FontAwesomeIcon icon={faTrophy} /> Adjudicaciones
                </li>
              </NavLink>
              <NavLink to="/clientes">
                <li>
                  <FontAwesomeIcon icon={faUsers} /> Clientes
                </li>
              </NavLink>
              <NavLink to="/usuarios">
                <li>
                  <FontAwesomeIcon icon={faTable} /> Usuarios
                </li>
              </NavLink>
              <NavLink to="/diccionario-cpvs">
                <li>
                  <FontAwesomeIcon icon={faBook} /> Diccionario CPVS
                </li>
              </NavLink>
              <hr />
            </>
          )}

          {/* Menú extra si técnico */}
          {userRole === "tecnico" && (
            <>
              <NavLink to="/" end>
                <li>
                  <FontAwesomeIcon icon={faHome} /> Inicio
                </li>
              </NavLink>
              <NavLink to="/concursos">
                <li>
                  <FontAwesomeIcon icon={faLandmark} /> Concursos
                </li>
              </NavLink>
              <NavLink to="/adjudicaciones">
                <li>
                  <FontAwesomeIcon icon={faTrophy} /> Adjudicaciones
                </li>
              </NavLink>
              <NavLink to="/clientes">
                <li>
                  <FontAwesomeIcon icon={faUsers} /> Clientes
                </li>
              </NavLink>
              <NavLink to="/diccionario-cpvs">
                <li>
                  <FontAwesomeIcon icon={faBook} /> Diccionario CPVS
                </li>
              </NavLink>
              <hr />
            </>
          )}

          {/* Menú extra si cliente */}
          {userRole === "cliente" && (
            <>
              <NavLink to="/" end>
                <li>
                  <FontAwesomeIcon icon={faHome} /> Inicio
                </li>
              </NavLink>
              <NavLink to="/concursos">
                <li>
                  <FontAwesomeIcon icon={faLandmark} /> Concursos
                </li>
              </NavLink>
              <NavLink to="/adjudicaciones">
                <li>
                  <FontAwesomeIcon icon={faTrophy} /> Adjudicaciones
                </li>
              </NavLink>
              <hr />
            </>
          )}

          {/* Menú común */}
          <NavLink to="/favoritos">
            <li>
              <FontAwesomeIcon icon={faHeart} /> Favoritos
              <Badge number={counts.countFavoritos} />
            </li>
          </NavLink>
          <NavLink to="/bajas-estadisticas">
            <li>
              <FontAwesomeIcon icon={faChartLine} /> Bajas estadísticas
              <Badge number={counts.countBajasEstadisticas} />
            </li>
          </NavLink>
          <NavLink to="/soportes-tecnicos">
            <li>
              <FontAwesomeIcon icon={faTools} /> Soportes técnicos
              <Badge number={counts.countSoportesTecnicos} />
            </li>
          </NavLink>
          <NavLink to="/soportes-administrativos">
            <li>
              <FontAwesomeIcon icon={faPaste} /> Soportes administrativos
              <Badge number={counts.countSoportesAdministrativos} />
            </li>
          </NavLink>
          <NavLink to="/soportes-juridicos">
            <li>
              <FontAwesomeIcon icon={faGavel} /> Soportes jurídicos
              <Badge number={counts.countSoportesJuridicos} />
            </li>
          </NavLink>
          <hr />
          <Link to="https://oclemconcursospublicos.com" target="_blank">
            <li>
              <FontAwesomeIcon icon={faFolder} /> Clasificación
            </li>
          </Link>
          <Link to="/" onClick={handleLogout}>
            <li>
              <FontAwesomeIcon icon={faPowerOff} /> Cerrar sesión
            </li>
          </Link>
        </ul>
      </aside>

      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}

export default LayoutWithSidebar;
